import { HttpTransportType, HubConnection } from '@microsoft/signalr';
import { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { LogLevel } from 'react-signalr';
import { retrieveUserFromStorage } from '../hooks/useSessionUser';
import { RootState } from '../store';
import EnvWindow from '../utils/env';
import { Context, Hub } from 'react-signalr/lib/signalr/types';

interface SignalRCustomProviderProps<H extends Hub> {
  children: ReactNode;
  context: Context<H>;
  url: string;
  disabled?: boolean;
  onConnectionChanged?: (connected: boolean) => void;
}
export const SignalRCustomProvider = <H extends Hub>({
  children,
  context,
  url,
  disabled,
  onConnectionChanged,
}: SignalRCustomProviderProps<H>): JSX.Element => {
  const user = useSelector((state: RootState) => state.auth.user);

  const accessTokenFactory = () => {
    if (user) {
      return user.access_token;
    } else {
      const auth = retrieveUserFromStorage();
      return auth ? auth.access_token : '';
    }
  };

  const devEnvProps = EnvWindow.REACT_APP_DXC_ENV
    ? {
        logger: 2,
        onOpen: (conn: HubConnection) => {
          onConnectionChanged && onConnectionChanged(true);
          console.log('Opened connection id: ', conn.connectionId);
        },
        onClosed: (e?: Error) => {
          onConnectionChanged && onConnectionChanged(false);
          console.log('Closed WS connection.\n', e);
        },
        onError: (e?: Error) => new Promise(() => e !== undefined).then(() => console.error('WS Error:\n', e)),
      }
    : {
        onOpen: () => onConnectionChanged && onConnectionChanged(true),
        onClosed: () => onConnectionChanged && onConnectionChanged(false),
      };

  return (
    <context.Provider
      connectEnabled={!disabled && !!(user && user.access_token)}
      url={url}
      accessTokenFactory={accessTokenFactory}
      dependencies={[user]}
      skipNegotiation
      transport={HttpTransportType.WebSockets}
      {...devEnvProps}
    >
      {children}
    </context.Provider>
  );
};
